import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, withModifiers as _withModifiers, createElementVNode as _createElementVNode, createBlock as _createBlock } from "vue"

import { QTable, QTableProps } from "quasar"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import SfxRadioSimple from "src/components/radio/SfxRadioSimple.vue"
import { TABLE_COLUMN_WORK_STATE } from "src/util/constants/table"
import { RadioItemTable } from "src/util/types/components/sfxRadioList"
import {
  RowData,
  StateColumnNames,
  WorkStateOptions,
  WorkStateTableRowElement,
} from "src/util/types/components/sfxTable"
import { computed, PropType, ref, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits

const defaultSort = "unreviewed"

export default /*#__PURE__*/_defineComponent({
  __name: 'SfxTableThWorkState',
  props: {
  pagination: {
    type: Object as PropType<QTableProps["pagination"]>,
    required: true,
  },

  table: {
    type: Object as PropType<QTable>,
    required: true,
  },
},
  emits: ["update:pagination", "update:table"],
  setup(__props, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit

__expose({ sortState })

//endregion

//region Variables

const columnName = TABLE_COLUMN_WORK_STATE
const { t } = useI18n()

const pagination = computed({
  get() {
    return props.pagination
  },

  set(value) {
    return emit("update:pagination", value)
  },
})

const table = computed({
  get() {
    return props.table
  },

  set(value) {
    return emit("update:table", value)
  },
})

const stateMenuOpen = ref(false)
const stateButtonText = t("sfxTable.state_dropdown")
const sortByText = t("sfxTable.state_sort_by")
const sortModel = ref<StateColumnNames>(defaultSort)

const allSortEntries: RadioItemTable[] = [
  {
    label: t("sfxTable.state_latest"),
    val: "latest",
    labelClass: "sfx-radio-simple-label--italic",
  },
  { label: t("sfxTable.state_unreviewed"), val: "unreviewed" },
  { label: t("sfxTable.state_not_applicable"), val: "not_applicable" },
  { label: t("sfxTable.state_accepted"), val: "accepted" },
  { label: t("sfxTable.state_rejected"), val: "rejected" },
  {
    label: t("sfxTable.state_oldest"),
    val: "oldest",
    labelClass: "sfx-radio-simple-label--italic",
  },
]

const stateClass = computed(() => {
  return {
    "sfx-table__th-state-open": stateMenuOpen.value,
    "sfx-table__th-state-active":
      !stateMenuOpen.value &&
      pagination.value?.sortBy === columnName &&
      sortModel.value !== defaultSort,
  }
})

//endregion

//region Functions

async function triggerStateSort() {
  if (!table.value || !pagination.value) return

  await table.value.sort(columnName)

  pagination.value.sortBy = columnName
}

function sortState(a: RowData, b: RowData) {
  const colAState = a[columnName]
  const colBState = b[columnName]

  if (!colBState) return -1
  if (!colAState) return +1

  switch (sortModel.value) {
    case "latest":
      return sortByDate(colAState, colBState, 1)
    case "unreviewed":
      return sortByStateOption(colAState, colBState, "unreviewed")
    case "not_applicable":
      return sortByStateOption(colAState, colBState, "not_applicable")
    case "accepted":
      return sortByStateOption(colAState, colBState, "accepted")
    case "rejected":
      return sortByStateOption(colAState, colBState, "rejected")
    case "oldest":
      return sortByDate(colAState, colBState, -1)
    default:
      return 0
  }
}

function sortByStateOption(
  stateA: WorkStateTableRowElement,
  stateB: WorkStateTableRowElement,
  stateOption: WorkStateOptions
) {
  if (stateA.column === stateOption && stateB.column === stateOption) {
    return new Date(stateB.sortData).getTime() - new Date(stateA.sortData).getTime()
  } else {
    return stateB.column === stateOption ? 1 : -1
  }
}

function sortByDate(
  stateA: WorkStateTableRowElement,
  stateB: WorkStateTableRowElement,
  direction: -1 | 1
) {
  return (new Date(stateB.sortData).getTime() - new Date(stateA.sortData).getTime()) * direction
}

//endregion

//region Setup Code

watch(
  () => sortModel.value,
  async (newSortVal, oldSortValue) => {
    if (newSortVal && newSortVal !== oldSortValue) {
      await triggerStateSort()
    }
  },
  { immediate: true }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_q_menu = _resolveComponent("q-menu")!
  const _component_q_th = _resolveComponent("q-th")!

  return (_openBlock(), _createBlock(_component_q_th, {
    onTouchstart: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"])),
    onMousedown: _cache[5] || (_cache[5] = _withModifiers(() => {}, ["stop"])),
    onClick: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["prevent","stop"])),
    class: "sfx-table__th-state no-pointer-events"
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent","stop"]))
      }, [
        _createVNode(SfxBtnOutline, {
          label: _unref(stateButtonText),
          dense: "",
          onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["prevent","stop"])),
          class: _normalizeClass(["q-px-md all-pointer-events", stateClass.value])
        }, {
          default: _withCtx(() => [
            _createVNode(_component_q_menu, {
              modelValue: stateMenuOpen.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((stateMenuOpen).value = $event)),
              anchor: "bottom right",
              self: "top right",
              offset: [0, 10],
              "auto-close": "",
              class: "sfx-table__th-state-menu"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_q_list, {
                  dense: "",
                  padding: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_q_item, { class: "text-subtitle2 text-fun-label-and-icon" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(sortByText)), 1)
                      ]),
                      _: 1
                    }),
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(allSortEntries, (entry) => {
                      return _createVNode(SfxRadioSimple, {
                        key: entry.val,
                        options: entry,
                        modelValue: sortModel.value,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((sortModel).value = $event)),
                        class: _normalizeClass(entry.labelClass)
                      }, null, 8, ["options", "modelValue", "class"])
                    }), 64))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }, 8, ["label", "class"])
      ])
    ]),
    _: 1
  }))
}
}

})